"use client"

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"
import { DEFAULT_PALETTE, type ChartDimension } from "./types"
import type { CSSProperties } from "react"

/** A node in the sunburst hierarchy — has a name, a numeric size at the
 *  leaf, and optional children. Internal nodes' sizes are computed from
 *  the sum of their children when not provided. */
export interface SunburstNode {
  name: string
  size?: number
  children?: SunburstNode[]
  fill?: string
}

export interface SunburstChartProps {
  data: SunburstNode[]
  palette?: string[]
  showTooltip?: boolean
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
  /** Outer radius of the entire sunburst (defaults to "85%"). */
  outerRadius?: number | string
}

interface FlatRow { name: string; value: number; depth: number; fill: string }

/** Recursively flatten the tree per ring depth. Each ring is rendered as
 *  its own concentric Pie. */
function flatten(nodes: SunburstNode[], depth: number, palette: string[], colorOffset = 0): FlatRow[][] {
  const out: FlatRow[][] = []
  const ring: FlatRow[] = []
  let nextLevel: SunburstNode[] = []
  let i = 0
  for (const n of nodes) {
    const sum = n.children?.reduce((s, c) => s + (c.size ?? 0), 0) ?? 0
    const v = n.size ?? sum ?? 0
    ring.push({ name: n.name, value: v, depth, fill: n.fill || palette[(colorOffset + i) % palette.length] })
    if (n.children?.length) nextLevel = nextLevel.concat(n.children)
    i++
  }
  out.push(ring)
  if (nextLevel.length) out.push(...flatten(nextLevel, depth + 1, palette, colorOffset))
  return out
}

export function SunburstChart({
  data, palette = DEFAULT_PALETTE, showTooltip = true,
  className, style, height = "100%", width = "100%", outerRadius = "85%",
}: SunburstChartProps) {
  const rings = flatten(data, 0, palette)
  // Each ring takes a slice of the radial space. With N rings and an
  // outer radius R, ring k spans (k/N)*R..((k+1)/N)*R.
  const N = rings.length || 1
  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <PieChart>
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {rings.map((ring, k) => {
          const inner = `${Math.round((k / N) * 85)}%`
          const outer = k === N - 1
            ? (typeof outerRadius === "string" ? outerRadius : `${outerRadius}px`)
            : `${Math.round(((k + 1) / N) * 85)}%`
          return (
            <Pie
              key={k}
              data={ring}
              dataKey="value"
              nameKey="name"
              cx="50%" cy="50%"
              innerRadius={inner}
              outerRadius={outer}
              isAnimationActive={false}
              label={false}
            >
              {ring.map((r, i) => <Cell key={i} fill={r.fill} stroke="hsl(var(--card))" strokeWidth={1} />)}
            </Pie>
          )
        })}
      </PieChart>
    </ResponsiveContainer>
  )
}
